/**
 * Add Template Component
 * 
 * Form to create new device templates from scratch.
 * Created templates are saved directly as 'approved' status.
 */

import React, { useState } from 'react';
import { Card, Flex, Text, Button, Heading } from '@radix-ui/themes';
import { DeviceTemplate } from '../../types';
import { adminAPI } from '../../services/api';
import TemplateFormFields from '../TemplateFormFields';
import { v4 as uuidv4 } from 'uuid';

interface AddTemplateProps {
  adminKey: string;
  onSuccess?: () => void;
}

const AddTemplate: React.FC<AddTemplateProps> = ({ adminKey, onSuccess }) => {
  const [formData, setFormData] = useState<Partial<DeviceTemplate>>({
    type: 'camera',
    name: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    // Validation
    if (!formData.name?.trim()) {
      alert('❌ Please enter a template name');
      return;
    }

    if (!formData.type) {
      alert('❌ Please select a device type');
      return;
    }

    try {
      setSaving(true);

      // Create new template with approved status
      const newTemplate: DeviceTemplate = {
        id: uuidv4(),
        name: formData.name.trim(),
        type: formData.type,
        brand: formData.brand?.trim(),
        model: formData.model?.trim(),
        description: formData.description?.trim(),
        powerInput: formData.powerInput,
        powerOutputs: formData.powerOutputs || [],
        batteryCapacity: formData.batteryCapacity,
        usbInputs: formData.usbInputs || [],
        usbOutputs: formData.usbOutputs || [],
        status: 'approved', // New templates are immediately approved
        isBuiltIn: false, // Admin-created templates are not built-in
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Submit via update endpoint (works for new templates too)
      await adminAPI.updateTemplate(newTemplate.id, newTemplate, adminKey);

      // Reset form
      setFormData({
        type: 'camera',
        name: '',
      });

      onSuccess?.();
      alert('✅ Template created successfully!');
    } catch (error: any) {
      console.error('Failed to create template:', error);
      alert(`❌ Failed to create template: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Flex direction="column" gap="4">
        <Heading size="4">Create New Template</Heading>
        <Text size="2" color="gray">
          Add a new device template to the library. It will be immediately available to all users.
        </Text>

        <TemplateFormFields
          template={formData}
          onChange={(updated) => setFormData(prev => ({ ...prev, ...updated }))}
          showTypeSelector={true}
        />

        <Flex gap="2" justify="end">
          <Button
            onClick={handleSave}
            disabled={saving || !formData.name?.trim()}
          >
            {saving ? 'Creating...' : 'Create Template'}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};

export default AddTemplate;
